/**
 * Practice-focus picker — the pure decision logic behind the Dashboard's
 * "Practice this domain" / "Practice missed" / "Practice unanswered" /
 * "Drill weakest domain" entry points on BOTH apps.
 *
 * Every entry point hands the modal one question pool (a domain's questions,
 * or the whole exam). The pool is split into three buckets the user picks from:
 *   unanswered — never answered (no record, or times_answered < 1)
 *   incorrect  — answered at least once and the latest score is below 1
 *   all        — the whole pool
 * A question that was wrong once but right later is neither unanswered nor
 * incorrect; it is only reachable through "all".
 *
 * Classification is delegated to `computeQuestionPools` (readiness), the
 * repo's single source of truth for unanswered/incorrect. That keeps the chip
 * counts byte-identical to the domain card's Incorrect/Unanswered numbers,
 * including its String-normalised id matching (score records persist
 * question_id with mixed string|number types) and its bundle-anchoring (score
 * records for ids outside the pool are ignored).
 *
 * FREE TIER (FREE_SET_ENFORCED): the dashboard is one of the free "content
 * doors", so it follows the Custom Test rule exactly. When the caller passes
 * `freeSetIds` (the bounded free set, null for premium / flag off), EVERY
 * bucket — unanswered, incorrect and all — is drawn from free set ∩ pool.
 * Incorrect is deliberately NOT exempted (decision 2026-09-18): this door has no
 * per-session cap, so a lapsed subscriber could otherwise keep drilling their
 * whole paid-era history for free with little reason to resubscribe. (Custom
 * Test still exempts incorrect/bookmarked under its 5-question free cap; the
 * cross-door policy for lapsed subscribers is backlog #63.)
 * Exhaustion mirrors Quick Set: once a free user has answered every free
 * question in the pool, starting "All" (repeats) first shows the shared
 * exhaustion modal; Incorrect is a review by intent and never triggers it.
 *
 * The session is a uniform random sample from the CHOSEN bucket only. There is
 * deliberately no ordering and no dedupe across sessions.
 */

import { computeQuestionPools } from '../readiness/questionPools';
import { fisherYates } from '../readiness/fisherYates';

export const PRACTICE_FOCUS = Object.freeze({
  UNANSWERED: 'unanswered',
  INCORRECT: 'incorrect',
  ALL: 'all',
});

/** Chip order in the modal on both apps. */
export const PRACTICE_FOCUS_ORDER = Object.freeze([
  PRACTICE_FOCUS.UNANSWERED,
  PRACTICE_FOCUS.INCORRECT,
  PRACTICE_FOCUS.ALL,
]);

/** Chip copy — single source for web + mobile. */
export const PRACTICE_FOCUS_LABELS = Object.freeze({
  [PRACTICE_FOCUS.UNANSWERED]: 'Unanswered',
  [PRACTICE_FOCUS.INCORRECT]: 'Incorrect',
  [PRACTICE_FOCUS.ALL]: 'All',
});

/** Session-size chips, and the size preselected when the modal opens. */
export const PRACTICE_SESSION_SIZES = Object.freeze([10, 25, 50]);
export const PRACTICE_DEFAULT_SESSION_SIZE = 25;

// The pool is already exactly the set to classify, so no topic filter.
function classify(pool, allQuestionsScores) {
  return computeQuestionPools(pool, allQuestionsScores ?? [], null);
}

/**
 * The items whose id is in the bounded free set (type-insensitive ids, input
 * order kept). `null` / non-array `freeSetIds` = no restriction (premium / flag
 * off) and the SAME array comes back. `getId` adapts the id field (a question's
 * `id`, a score record's `question_id`, …) so every free-set intersection in the
 * apps goes through this one helper.
 *
 * @template T
 * @param {T[]} items
 * @param {Array|null} [freeSetIds]
 * @param {(item: T) => any} [getId]
 * @returns {T[]}
 */
export function restrictToFreeSet(items, freeSetIds, getId = (item) => item?.id) {
  if (!Array.isArray(freeSetIds)) return items;
  const free = new Set(freeSetIds.map((id) => String(id)));
  return (items ?? []).filter((item) => free.has(String(getId(item))));
}

function bucketPool(pool, allQuestionsScores, freeSetIds) {
  const freePool = restrictToFreeSet(pool, freeSetIds);
  const pools = classify(freePool, allQuestionsScores);
  return { unanswered: pools.unanswered, incorrect: pools.incorrect, all: freePool };
}

/**
 * Split a pool into the three focus buckets. `all` keeps the pool's order.
 * Thin wrapper over the readiness classifier so the modal and the domain card
 * can never disagree on what "incorrect" / "unanswered" means.
 *
 * With `freeSetIds` (free user), all three buckets come from the free slice of
 * the pool — including `incorrect` (see the module header).
 *
 * @param {Array} questions           the pool [{ id, topic_id, ... }]
 * @param {Array} [allQuestionsScores] profile score records
 * @param {Array|null} [freeSetIds]   bounded free set ids, or null
 * @returns {{ unanswered: Array, incorrect: Array, all: Array }}
 */
export function bucketQuestions(questions, allQuestionsScores, freeSetIds) {
  const pool = Array.isArray(questions) ? questions.filter(Boolean) : [];
  return bucketPool(pool, allQuestionsScores, freeSetIds);
}

/**
 * Which chip is preselected when the modal opens.
 * A `preferredFocus` (a tile like "Practice missed" forces Incorrect) wins when
 * its bucket has questions; otherwise fall through Unanswered → Incorrect → All.
 * `all` is only preselected when both other buckets are empty.
 *
 * @param {{ unanswered: Array, incorrect: Array, all: Array }} buckets
 * @param {string} [preferredFocus] one of PRACTICE_FOCUS
 * @returns {string} one of PRACTICE_FOCUS
 */
export function pickDefaultFocus(buckets, preferredFocus) {
  const len = (key) => (Array.isArray(buckets?.[key]) ? buckets[key].length : 0);
  if (preferredFocus && PRACTICE_FOCUS_ORDER.includes(preferredFocus) && len(preferredFocus) > 0) {
    return preferredFocus;
  }
  if (len(PRACTICE_FOCUS.UNANSWERED) > 0) return PRACTICE_FOCUS.UNANSWERED;
  if (len(PRACTICE_FOCUS.INCORRECT) > 0) return PRACTICE_FOCUS.INCORRECT;
  return PRACTICE_FOCUS.ALL;
}

/**
 * The number of questions the Start button launches: the chosen size clamped
 * to what the bucket actually holds (never negative, 0 only for an empty bucket).
 *
 * @param {number} requestedSize a PRACTICE_SESSION_SIZES value
 * @param {number} bucketLength
 * @returns {number}
 */
export function clampSessionSize(requestedSize, bucketLength) {
  const size = Number(requestedSize) > 0 ? Math.floor(Number(requestedSize)) : 0;
  const available = Number(bucketLength) > 0 ? Math.floor(Number(bucketLength)) : 0;
  return Math.min(size, available);
}

/**
 * The size chip to show as selected after the focus changes: keep the user's
 * choice when the new bucket can honour it, otherwise drop to the largest chip
 * the bucket can fill. When even the smallest chip is too big, the smallest
 * chip stays selected and `sessionSizeChips` relabels it with the bucket length.
 *
 * @param {number} selectedSize  current chip
 * @param {number} bucketLength
 * @param {ReadonlyArray<number>} [sizes]
 * @returns {number}
 */
export function resolveSessionSize(selectedSize, bucketLength, sizes = PRACTICE_SESSION_SIZES) {
  const available = Number(bucketLength) > 0 ? Number(bucketLength) : 0;
  if (sizes.includes(selectedSize) && selectedSize <= available) return selectedSize;
  const fitting = sizes.filter((s) => s <= available);
  return fitting.length ? fitting[fitting.length - 1] : sizes[0];
}

/**
 * The render model for the size row. A chip is disabled when the bucket can't
 * fill it — except the smallest chip, which stays enabled whenever the bucket
 * has anything and shows the clamped count as its label (so a 7-question
 * bucket renders "7 | 25 | 50" with "7" selectable, never a selected-but-
 * disabled chip). `selected` is the requested size; `count` is what Start
 * launches for that chip.
 *
 * @param {number} availableCount   the selected bucket's length
 * @param {number} selectedSize     the requested chip (from resolveSessionSize)
 * @param {ReadonlyArray<number>} [sizes]
 * @returns {Array<{ size: number, label: string, count: number, disabled: boolean, selected: boolean }>}
 */
export function sessionSizeChips(availableCount, selectedSize, sizes = PRACTICE_SESSION_SIZES) {
  const available = Number(availableCount) > 0 ? Number(availableCount) : 0;
  const smallest = sizes[0];
  return sizes.map((size) => {
    const count = clampSessionSize(size, available);
    const isSmallest = size === smallest;
    const disabled = available === 0 || (size > available && !isSmallest);
    return {
      size,
      label: String(isSmallest && available > 0 ? count : size),
      count,
      disabled,
      selected: size === selectedSize,
    };
  });
}

/**
 * Uniform random sample of `count` questions from ONE bucket. Never returns a
 * question outside the bucket; returns the whole bucket when count exceeds it.
 *
 * @param {Array} bucket
 * @param {number} count
 * @param {() => number} [random] 0 ≤ r < 1 source; defaults to Math.random
 * @returns {Array}
 */
export function samplePractice(bucket, count, random = Math.random) {
  const pool = Array.isArray(bucket) ? bucket : [];
  const n = clampSessionSize(count, pool.length);
  if (n === 0) return [];
  return fisherYates(pool, random).slice(0, n);
}

/**
 * Copy shown under the focus chips for a free user, explaining why the chip
 * counts are smaller than the domain card's numbers, plus the upsell CTA.
 * One source for web + mobile.
 *
 * @param {{ freeCount: number, fullCount: number }} access
 * @returns {{ note: string, upgrade: string }}
 */
export function freeAccessCopy({ freeCount, fullCount } = {}) {
  return {
    note: `${freeCount} of ${fullCount} questions in this set are free.`,
    upgrade: `Unlock all ${fullCount} questions`,
  };
}

/**
 * Same trigger as Quick Set: a free user whose free slice of this pool has no
 * unseen question left, starting "All" (which can only repeat). Incorrect is a
 * review by intent and never triggers it; Unanswered can't be started when
 * exhausted (its bucket is empty).
 *
 * @param {{ access?: { restricted: boolean, exhausted: boolean } }} session
 * @param {string} focus the focus being started
 * @returns {boolean}
 */
export function shouldShowExhaustion(session, focus) {
  const access = session?.access;
  return !!access?.restricted && !!access?.exhausted && focus === PRACTICE_FOCUS.ALL;
}

/**
 * Which analytics an exhaustion prompt on this door should emit. Every other
 * door means "the EXAM's free set is spent" by `free_pool_exhausted`, so a
 * per-domain exhaustion only emits it when the exam-wide free set is spent too;
 * `exhaustion_modal_shown` always fires, tagged with the scope.
 *
 * @param {{ poolScope: 'domain'|'all', examExhausted: boolean }} p
 * @returns {{ scope: 'domain'|'exam', fireFreePoolExhausted: boolean }}
 */
export function exhaustionPlan({ poolScope, examExhausted } = {}) {
  const scope = poolScope === 'domain' ? 'domain' : 'exam';
  return { scope, fireFreePoolExhausted: scope === 'exam' || !!examExhausted };
}

/**
 * The same session re-targeted at another preferred focus (an exam-wide tile
 * reusing the memoised exam session instead of re-classifying the bank).
 * Only `defaultFocus` / `defaultSize` change; buckets, counts and access are
 * shared by reference.
 *
 * @param {ReturnType<typeof buildPracticeSession>} session
 * @param {string} [preferredFocus]
 */
export function withPreferredFocus(session, preferredFocus) {
  const defaultFocus = pickDefaultFocus(session.buckets, preferredFocus);
  return {
    ...session,
    defaultFocus,
    defaultSize: resolveSessionSize(PRACTICE_DEFAULT_SESSION_SIZE, session.counts[defaultFocus]),
  };
}

/**
 * Everything the modal needs from one entry point, computed once.
 * `canOpen` is false when the (free-restricted) pool is empty (the caller must
 * not open the modal); `access.restricted` is true only when the free slice is
 * actually smaller than the pool (an uncapped domain shows no note / upsell); `defaultSize` is the size chip preselected for `defaultFocus`;
 * `access` describes the free-tier restriction (`restricted` false = premium /
 * flag off, counts then equal the full pool).
 *
 * @param {Object} inputs
 * @param {Array}  inputs.questions           the pool
 * @param {Array}  [inputs.allQuestionsScores] profile score records
 * @param {string} [inputs.preferredFocus]    a tile-forced focus
 * @param {Array|null} [inputs.freeSetIds]    bounded free set ids (free user), else null
 * @returns {{ buckets, counts: {unanswered:number, incorrect:number, all:number},
 *            defaultFocus: string, defaultSize: number, canOpen: boolean,
 *            access: { restricted: boolean, freeCount: number, fullCount: number, exhausted: boolean } }}
 */
export function buildPracticeSession({ questions, allQuestionsScores, preferredFocus, freeSetIds } = {}) {
  const pool = Array.isArray(questions) ? questions.filter(Boolean) : [];
  const buckets = bucketPool(pool, allQuestionsScores, freeSetIds);
  const counts = {
    unanswered: buckets.unanswered.length,
    incorrect: buckets.incorrect.length,
    all: buckets.all.length,
  };
  const restricted = Array.isArray(freeSetIds) && counts.all < pool.length;
  const defaultFocus = pickDefaultFocus(buckets, preferredFocus);
  return {
    buckets,
    counts,
    defaultFocus,
    defaultSize: resolveSessionSize(PRACTICE_DEFAULT_SESSION_SIZE, counts[defaultFocus]),
    canOpen: counts.all > 0,
    access: {
      restricted,
      freeCount: counts.all,
      fullCount: pool.length,
      // Same predicate as isFreeQuestionSetExhausted (non-empty set, nothing unseen),
      // read off the counts instead of classifying the pool a second time.
      exhausted: restricted && counts.all > 0 && counts.unanswered === 0,
    },
  };
}
